import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";

/**
 * VoidReasonDialog — Feb 2026
 *
 * Collects a required reason before stamping a Void. Preset chips make
 * the field machine-filterable in reports (dupe / cancelled_by_client /
 * data_entry_error / other), while the free-text note goes into the
 * revision log so a future auditor can see why a document was pulled.
 */
const PRESETS = [
  { key: "dupe",                label: "Duplicate",             hint: "Issued twice by mistake" },
  { key: "cancelled_by_client", label: "Cancelled by client",   hint: "Buyer backed out" },
  { key: "data_entry_error",    label: "Data-entry error",      hint: "Wrong amount / party / HSN" },
  { key: "other",               label: "Other",                 hint: "Describe below" },
];

export default function VoidReasonDialog({ open, doc, ws, onClose, onDone }) {
  const [preset, setPreset] = useState("dupe");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const label = (doc?.doc_type || "document").replace(/_/g, " ");

  const submit = async () => {
    const trimmed = note.trim();
    if (!trimmed) { toast.error("Please add a short note explaining the void"); return; }
    setBusy(true);
    try {
      await api.post(`/gst/workspaces/${ws}/documents/${doc.id}/void`, {
        preset, note: trimmed,
      });
      toast.success(`${doc.doc_no || "Document"} voided`);
      onDone?.();
      onClose?.();
    } catch (e) { toast.error(apiErrorMessage(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent className="max-w-md rounded-none" data-testid="void-reason-dialog">
        <DialogHeader>
          <DialogTitle>Void {label} {doc?.doc_no}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label className="text-[11px] uppercase tracking-widest text-muted-foreground">Reason</Label>
            <div className="mt-1 grid grid-cols-2 gap-1.5">
              {PRESETS.map((p) => (
                <button key={p.key}
                        type="button"
                        onClick={() => setPreset(p.key)}
                        className={`text-left px-2.5 py-1.5 border text-[11px] rounded-none ${
                          preset === p.key
                            ? "border-primary bg-primary/10 text-primary font-semibold"
                            : "border-border hover:bg-muted/60 text-muted-foreground"
                        }`}
                        data-testid={`void-reason-${p.key}`}>
                  <div className="font-medium">{p.label}</div>
                  <div className="text-[10px] text-muted-foreground mt-0.5">{p.hint}</div>
                </button>
              ))}
            </div>
          </div>
          <div>
            <Label htmlFor="void-note" className="text-[11px] uppercase tracking-widest text-muted-foreground">
              Note <span className="text-rose-600">*</span>
            </Label>
            <Textarea id="void-note" rows={3}
                      className="rounded-none text-sm mt-1"
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                      placeholder="e.g. Issued twice because of double submission on Feb 09"
                      maxLength={500}
                      data-testid="void-reason-note" />
            <div className="text-[10px] text-muted-foreground mt-1 text-right">
              {note.length}/500
            </div>
          </div>
          <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 px-3 py-2">
            Payments already recorded stay on the ledger. To refund the customer, issue a Credit Note.
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-none" onClick={onClose}
                  data-testid="void-reason-cancel">Cancel</Button>
          <Button className="rounded-none bg-amber-600 hover:bg-amber-700 text-white"
                  onClick={submit}
                  disabled={busy || !note.trim()}
                  data-testid="void-reason-confirm">
            {busy ? "Voiding…" : "Void document"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
